// table.js
import React from 'react';
import { Space, Tooltip } from 'antd';
import { ReactComponent as HorizontalDotsIcon } from '../../../../assets/icons/horizontalDots.svg';
import { ReactComponent as LeftIcon } from '../../../../assets/icons/chevron-left.svg';
import styles from './ResultsTable.module.scss';

// Helper function to parse DD/MM/YYYY dates
const parseDate = dateString => {
  if (!dateString) return new Date(0);
  const [day, month, year] = dateString.split('/');
  return new Date(year, month - 1, day);
};

export const getColumns = (onMoreClick, id) => {
  const commonColumns = [
    {
      title: '',
      key: 'expand',
      width: 30,
      className: styles.expandIconCell,
      render: () => null,
    },
    {
      title: 'A/A',
      dataIndex: 'aa',
      key: 'aa',
      width: 70,
      className: styles.colAA,
      sorter: (a, b) => a.aa - b.aa,
      render: (text, record) => (record.isDetail ? null : text),
    },
    {
      title: 'ΑΦΜ',
      dataIndex: 'afm',
      key: 'afm',
      width: 95,
      className: styles.colAFM,
      sorter: (a, b) => a.afm?.localeCompare(b.afm),
      render: (text, record) => (record.isDetail ? null : text),
    },
    {
      title: 'Επωνυμία',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      className: styles.colName,
      sorter: (a, b) => a.name?.localeCompare(b.name),
      render: (text, record) => (record.isDetail ? null : text),
    },
    {
      title: 'Status',
      dataIndex: 'activeTaskId',
      key: 'activeTaskId',
      width: 150,
      className: styles.colStatus,
      sorter: (a, b) => {
        const aStatus =
          a.activeTaskId === 'submit_application'
            ? 'Επεξεργασία Αίτησης'
            : 'Center of Excellence';
        const bStatus =
          b.activeTaskId === 'submit_application'
            ? 'Επεξεργασία Αίτησης'
            : 'Center of Excellence';
        return aStatus.localeCompare(bStatus);
      },
      render: (text, record) => {
        if (record.isDetail) return null;
        const statusText =
          record.activeTaskId === 'submit_application'
            ? 'Επεξεργασία Αίτησης'
            : 'Center of Excellence';
        return <span className={styles.statusBadge}>{statusText}</span>;
      },
    },
    {
      title: 'Τόπος Επένδυσης',
      dataIndex: 'investmentPlace',
      key: 'investmentPlace',
      width: 120,
      className: styles.colLocation,
      sorter: (a, b) => a.investmentPlace?.localeCompare(b.investmentPlace),
      render: (text, record) => (record.isDetail ? null : text),
    },
    {
      title: 'Επιλεγμένα Προγράμματα',
      dataIndex: 'programs',
      key: 'programs',
      width: 100,
      className: styles.colPrograms,
      sorter: (a, b) => a.programs?.[0]?.localeCompare(b.programs?.[0]),
      render: (programs = [], record) => {
        if (record.isDetail) {
          return (
            <div className={styles.programsExpanded}>
              <span className={`${styles.programBadge} ${styles.programMain}`}>
                {programs[1]?.title}
              </span>
            </div>
          );
        }

        return programs[0]?.title ? (
          <span className={`${styles.programBadge} ${styles.programMain}`}>
            {programs[0].title}
          </span>
        ) : null;
      },
    },
    {
      title: 'Συνολικός Προϋπολογισμός',
      dataIndex: 'fundingScheme',
      key: 'fundingScheme',
      width: 200,
      className: styles.colFunding,
      sorter: (a, b) => {
        const aVal = parseFloat(
          a.fundingScheme?.totalBudget?.replace(/[^0-9.-]+/g, '') || 0
        );
        const bVal = parseFloat(
          b.fundingScheme?.totalBudget?.replace(/[^0-9.-]+/g, '') || 0
        );
        return aVal - bVal;
      },
      render: (funding, record) => {
        if (!funding) return '-';
        if (record.isDetail) {
          return (
            <div className={styles.fundingExpanded}>
              {funding.grant && (
                <div className={styles.fundingRow}>
                  <span className={styles.fundingLabel}>Επιδότηση:</span>
                  <span className={styles.fundingValue}>{funding.grant}</span>
                </div>
              )}
              {funding.ownParticipation && (
                <div className={styles.fundingRow}>
                  <span className={styles.fundingLabel}>Ίδια Συμμετοχή:</span>
                  <span className={styles.fundingValue}>
                    {funding.ownParticipation}
                  </span>
                </div>
              )}
              {funding.bankLoan && (
                <div className={styles.fundingRow}>
                  <span className={styles.fundingLabel}>
                    Τραπεζικός Δανεισμός:
                  </span>
                  <span className={styles.fundingValue}>
                    {funding.bankLoan}
                  </span>
                </div>
              )}
            </div>
          );
        }
        return (
          <div className={styles.fundingCell}>
            <div>
              <strong>Συν. Προϋπ/γισμός:</strong>
            </div>
            <div>{funding.totalBudget}</div>
          </div>
        );
      },
    },
    {
      title: 'Ημ/νία Αίτησης',
      dataIndex: 'applicationDate',
      key: 'applicationDate',
      width: 100,
      className: styles.colDateApp,
      sorter: (a, b) =>
        parseDate(a.applicationDate) - parseDate(b.applicationDate),
      render: (text, record) => (record.isDetail ? null : text),
    },
    {
      title: 'Ημ/νία Υποβολής',
      dataIndex: 'submissionDate',
      key: 'submissionDate',
      width: 100,
      className: styles.colDateSub,
      sorter: (a, b) =>
        parseDate(a.submissionDate) - parseDate(b.submissionDate),
      render: (text, record) => (record.isDetail ? null : text),
    },
    {
      title: '',
      fixed: 'right',
      key: 'action',
      align: 'right',
      render: (text, record) => {
        if (record.isDetail) {
          return;
        }
        return (
          <div
            id={`action-cell-${record.businessKey}`}
            align="end"
            style={{ width: 'auto', marginRight: '2rem' }}
          >
            <Tooltip title="Actions">
              <HorizontalDotsIcon
                onClick={e =>
                  onMoreClick(record.id, record.aa, record.applicationId)(e)
                }
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return commonColumns;
};

export const ExpandIcon = ({ expanded, onExpand, record, expandable }) => {
  if (!record.children?.length || record.isDetail) return <span />;

  const handleClick = e => {
    e.stopPropagation();
    onExpand(record, e);
  };

  return (
    <button
      type="button"
      className={`${styles.expandIcon} ${expanded ? styles.expanded : ''}`}
      onClick={handleClick}
    >
      <LeftIcon />
    </button>
  );
};
